import { ref as dbRef, push, query, orderByChild, startAt, endAt, get, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import {
  ERROR_LOG_NODE,
  ERROR_LOG_BUFFER_KEY,
  ERROR_LOG_BUFFER_MAX_ENTRIES,
  ERROR_LOG_BUFFER_MAX_AGE_MS,
  ERROR_LOG_MAX_FLUSH_ATTEMPTS,
  ERROR_LOG_RETRY_BASE_MS,
  ERROR_LOG_SOURCES,
  ERROR_LOG_LEVELS,
  ERROR_LOG_DEDUPE_WINDOW_MS,
  ERROR_LOG_MAX_PER_SESSION,
  ERROR_LOG_MAX_PER_MINUTE,
  ERROR_LOG_FLUSH_INTERVAL_MS,
  ERROR_LOG_MAX_MESSAGE_LEN,
  ERROR_LOG_MAX_STACK_LEN,
  ERROR_LOG_MAX_CONTEXT_LEN,
  ERROR_LOG_IGNORE_PATTERNS,
  ERROR_LOG_WARN_ALLOW_PATTERNS,
  ERROR_LOG_RTDB_STATUS,
  ERROR_LOG_AUTH_GRACE_MS,
} from '../constants/bookingConst';
import { reportErrorToBackend, reportFailureBatchToBackend } from './errorLogRemoteUtil';

// ---------------------------------------------------------------------------
// /errorLogs — what the browser console saw, kept where an admin can read it
// back from a production deployment they have no console access to.
//
// WHY A DATABASE NODE AND NOT FILES:
// The obvious shape for "a folder of error.txt files" is Firebase Storage,
// one object per day. It was rejected because an object cannot be appended
// to — every flush would have to download the day's file, concatenate, and
// re-upload it, which costs more bandwidth per error than the record itself
// and races between two tabs writing at once. A record per error has neither
// problem, and the .txt is rendered from those records at DOWNLOAD time
// (buildErrorLogText below) rather than stored as text. The admin still gets
// a file per day; it just doesn't exist until they ask for it.
//
// WHY THE WRITE GUARDS ARE NOT OPTIONAL:
// This node is written to by guests, on production deployments, with no one
// watching. A render loop that throws produces errors as fast as React can
// re-render. So every write passes three independent ceilings — a dedupe
// window, a per-minute rate limit and a per-session cap (see the constants
// file) — and they are all per tab, so one broken client cannot flood the
// node for everyone. A tab that hits its cap stops writing and says so once.
//
// THE RE-ENTRANCY PROBLEM:
// The logger captures console.error. Writing to the database can fail, and
// failing prints a console.error. Left alone that is an infinite loop that
// writes until the quota is gone. Two things prevent it: `suspended`, which
// stops queueing once ERROR_LOG_MAX_FLUSH_ATTEMPTS consecutive writes have
// been rejected, and ERROR_LOG_IGNORE_PATTERNS, which drops anything this
// file prints.
//
// APPEND-ONLY, BUT PRUNABLE: database.rules.json allows a create and a
// delete on /errorLogs/$entryId, never an edit — an entry cannot be
// rewritten after the fact, but old days can be cleared out (see
// pruneErrorLogsOlderThan).
// ---------------------------------------------------------------------------

export {
  ERROR_LOG_SOURCES,
  ERROR_LOG_SOURCE_LABELS,
  ERROR_LOG_LEVELS,
  ERROR_LOG_NODE,
  DEFAULT_ERROR_LOG_DAYS,
  ERROR_LOG_RETENTION_DAYS,
} from '../constants/bookingConst';

/* ---- Module state ------------------------------------------------------ */

/** One id per tab per page load. Ties a burst of entries to one visitor. */
const SESSION_ID = Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

/** installGlobalErrorLogging() is idempotent; this is how. */
let installed = false;

/**
 * Set once this tab has failed ERROR_LOG_MAX_FLUSH_ATTEMPTS times in a row.
 * Capture keeps running (so the console still behaves normally) but nothing
 * more is queued — see the re-entrancy note above. Entries already captured
 * are NOT lost: they stay in the localStorage buffer for the next page load.
 */
let suspended = false;

/** Entries waiting for the next flush. */
let queue = [];
let flushTimer = null;
let flushInFlight = false;
let consecutiveFailures = 0;

/** fingerprint -> { lastWriteMs, suppressed } for the dedupe window. */
const seen = new Map();

/** Rolling one-minute window of write timestamps, for the rate limit. */
let writeTimestamps = [];

/** Records actually written by this tab, against ERROR_LOG_MAX_PER_SESSION. */
let sessionWriteCount = 0;

/** Set by the app so entries say which screen was open. See setErrorLogScreen. */
let currentScreen = null;

/** The untouched console methods, kept so the patch can still print. */
const nativeConsole = {
  error: typeof console !== 'undefined' ? console.error.bind(console) : () => {},
  warn: typeof console !== 'undefined' ? console.warn.bind(console) : () => {},
};

/* ---- Small helpers ----------------------------------------------------- */

/**
 * Truncates to `max` characters with a visible marker, so a clipped stack is
 * never mistaken for a short one.
 * @param {*} value
 * @param {number} max
 * @returns {string|null}
 */
const clip = (value, max) => {
  if (value === null || value === undefined) return null;
  const str = String(value);
  if (str.length <= max) return str;
  return `${str.slice(0, max)}… [truncated, ${str.length} chars]`;
};

/**
 * "2026-09-21" in the BROWSER's local time, which is the club's own timezone
 * on every deployment — a day boundary an admin recognises, not UTC's.
 * @param {number} ts
 * @returns {string}
 */
export const toDayKey = (ts) => {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Pulls a machine-readable code out of whatever was thrown. Firebase is the
 * reason this exists: `auth/invalid-credential` and `PERMISSION_DENIED` are
 * the fields that actually identify a failure, and both hide in different
 * places depending on which SDK threw.
 * @param {*} err
 * @param {string} message
 * @returns {string|null}
 */
const extractCode = (err, message) => {
  if (err && typeof err === 'object' && typeof err.code === 'string' && err.code) return err.code;
  const text = String(message || '');
  const match = text.match(/\b(PERMISSION_DENIED|permission_denied|UNAVAILABLE|NETWORK_ERROR|DISCONNECTED|EXPIRED_TOKEN|INVALID_TOKEN|MAX_RETRIES|OVERRIDDEN|UNKNOWN_ERROR)\b/);
  if (match) return match[1].toUpperCase();
  const sdkMatch = text.match(/\b((?:auth|storage|app-check|functions|firestore)\/[a-z0-9-]+)\b/);
  return sdkMatch ? sdkMatch[1] : null;
};

/**
 * Flattens console arguments into one string. Error objects contribute their
 * message rather than "[object Object]", which is what a naive join gives and
 * is exactly the case that matters most here.
 * @param {Array<*>} args
 * @returns {string}
 */
const stringifyArgs = (args) => (args || []).map((arg) => {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg);
  } catch (e) {
    // Circular references are common in framework objects; String() still
    // gives something, and losing one argument must not lose the entry.
    return String(arg);
  }
}).join(' ');

/**
 * Identity of an error for dedupe purposes: what it was, what it said, and
 * where it came from. The first stack frame is included so two different
 * call sites raising the same message stay distinct.
 * @param {string} source
 * @param {string} message
 * @param {string|null} stack
 * @returns {string}
 */
const fingerprintOf = (source, message, stack) => {
  const firstFrame = (stack || '').split('\n').find(line => line.trim().startsWith('at')) || '';
  return `${source}::${String(message).slice(0, 200)}::${firstFrame.trim().slice(0, 120)}`;
};

const matchesAny = (text, patterns) => patterns.some(p => text.includes(p));

/**
 * Records which admin screen is open, so an entry says where the user was.
 * Called from AdminPage on every screen change; harmless to leave unset on
 * the guest side, where `url` already identifies the page.
 * @param {string|null} screen
 * @returns {void}
 */
export const setErrorLogScreen = (screen) => { currentScreen = screen || null; };

/* ---- The unsent buffer -------------------------------------------------
 * /errorLogs is gated on `auth != null`, so an entry captured before the
 * silent sign-in lands — or while Auth is unreachable — has no one to be
 * written as. Dropping it would lose precisely the errors worth having: a
 * failure to authenticate is an outage for that visitor, and it is invisible
 * from the server side.
 *
 * So unsent entries go to localStorage and are retried: when sign-in resolves
 * a moment later (the ordinary boot race), and again on the visitor's next
 * page load. Every read and write here is wrapped — localStorage throws
 * outright in Safari private mode, and losing the buffer must never take the
 * app with it.
 * --------------------------------------------------------------------- */

/**
 * Reads the buffered entries, dropping anything past its age limit.
 * @returns {Array<object>}
 */
const readBuffer = () => {
  try {
    const raw = window.localStorage.getItem(ERROR_LOG_BUFFER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - ERROR_LOG_BUFFER_MAX_AGE_MS;
    return parsed.filter(e => e && typeof e.timestamp === 'number' && e.timestamp >= cutoff);
  } catch (e) {
    return [];
  }
};

/**
 * Replaces the buffer, keeping only the newest ERROR_LOG_BUFFER_MAX_ENTRIES.
 * Oldest-first eviction: when a device has been failing for a while, the
 * recent errors are the ones that still describe the current build.
 * @param {Array<object>} entries
 * @returns {void}
 */
const writeBuffer = (entries) => {
  try {
    const trimmed = entries.slice(-ERROR_LOG_BUFFER_MAX_ENTRIES);
    if (trimmed.length === 0) window.localStorage.removeItem(ERROR_LOG_BUFFER_KEY);
    else window.localStorage.setItem(ERROR_LOG_BUFFER_KEY, JSON.stringify(trimmed));
  } catch (e) {
    // Quota, private mode, or storage disabled. Nothing to do — the entries
    // stay in the in-memory queue for as long as this page lives.
  }
};

/** Moves a batch back to the buffer after a failed or impossible send. */
const bufferEntries = (entries) => {
  if (!entries || entries.length === 0) return;
  writeBuffer([...readBuffer(), ...entries].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)));
};

/* ---- Escalating a sign-in that never arrives --------------------------- */

let authEscalationTimer = null;
/** One escalation per page load; a second would just repeat the same report. */
let authEscalated = false;

/**
 * Starts (once) the countdown that turns "still nobody signed in" into a
 * reported failure.
 *
 * Cancelled the moment a user appears, so the ordinary boot race — error
 * captured, sign-in lands a second later — never triggers it. Only a sign-in
 * that genuinely does not happen gets here, which is exactly the outage the
 * database sink cannot record for itself.
 *
 * @returns {void}
 */
const scheduleAuthEscalation = () => {
  if (authEscalationTimer || authEscalated || suspended) return;

  authEscalationTimer = setTimeout(() => {
    authEscalationTimer = null;
    // Sign-in arrived while we waited; the normal flush path has it.
    if (auth?.currentUser) return;

    const stranded = readBuffer();
    if (stranded.length === 0) return;

    authEscalated = true;
    nativeConsole.warn(
      `[errorLog] no signed-in user after ${ERROR_LOG_AUTH_GRACE_MS / 1000}s; `
      + `${stranded.length} entr${stranded.length === 1 ? 'y is' : 'ies are'} still held locally. Reporting by email.`,
    );
    reportFailureBatchToBackend(
      stranded,
      `No authenticated user after ${ERROR_LOG_AUTH_GRACE_MS / 1000} seconds, so the database write was never possible. `
      + 'Sign-in itself is the most likely failure.',
      ERROR_LOG_RTDB_STATUS.UNAVAILABLE,
    );
    // Entries stay buffered on purpose: the email is a notification, not a
    // hand-off. If the visitor returns and signs in successfully, the backlog
    // still goes to the database where the admin screen can find it.
  }, ERROR_LOG_AUTH_GRACE_MS);
};

/* ---- Writing ----------------------------------------------------------- */

/**
 * Sends everything pending as ONE multi-path update, so a burst of errors
 * costs a single round trip. Pending means the in-memory queue PLUS anything
 * buffered from an earlier failure or an earlier page load.
 *
 * Never throws and never logs through the patched console. Three outcomes:
 *  - no signed-in user yet: nothing is sent and nothing is lost. The batch
 *    goes to the buffer, and the auth listener in installGlobalErrorLogging
 *    calls back here the moment a user appears.
 *  - write rejected: the batch goes back to the buffer and a backoff retry is
 *    scheduled. After ERROR_LOG_MAX_FLUSH_ATTEMPTS this tab stops trying, but
 *    the entries survive in localStorage for the next page load.
 *  - success: buffer cleared.
 *
 * @returns {Promise<void>}
 */
const flush = async () => {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (!db || flushInFlight) return;

  const pending = [...readBuffer(), ...queue];
  if (pending.length === 0) return;

  // No identity means the write WILL be rejected by the rules, so do not
  // spend an attempt on it. This is the ordinary case for anything that
  // failed during boot, before AuthGuard's silent sign-in resolved.
  //
  // But "wait for the auth callback" is only right when a sign-in is actually
  // coming. If AUTHENTICATION IS THE THING THAT BROKE, that callback never
  // fires: nothing retries, no attempt is ever counted as failed, the give-up
  // path is never reached, and the email that should announce the outage is
  // never sent — the entries just sit in localStorage. The escalation timer
  // below is what closes that hole.
  if (!auth?.currentUser) {
    queue = [];
    writeBuffer(pending);
    scheduleAuthEscalation();
    return;
  }

  flushInFlight = true;
  queue = [];
  // Cleared up front so a concurrent capture does not re-buffer what is
  // already in flight; restored below if the send fails.
  writeBuffer([]);

  try {
    const updates = {};
    pending.forEach((entry) => {
      // push() with no value only mints a key — no network call — which is
      // what makes a single update() possible for the whole batch.
      const key = push(dbRef(db, ERROR_LOG_NODE)).key;
      // `actor` is captured at queue time and is null for anything logged
      // before sign-in. Left as it was on purpose: "nobody was signed in when
      // this happened" is the finding, not a gap to backfill.
      updates[key] = entry;
    });
    await update(dbRef(db, ERROR_LOG_NODE), updates);
    consecutiveFailures = 0;
  } catch (e) {
    bufferEntries(pending);
    consecutiveFailures += 1;
    if (consecutiveFailures >= ERROR_LOG_MAX_FLUSH_ATTEMPTS) {
      suspended = true;
      // See the re-entrancy note at the top of this file: the ONE thing that
      // must not happen here is another captured error, so this goes through
      // the unpatched console.
      nativeConsole.warn(
        `[errorLog] ${consecutiveFailures} failed writes; logging is suspended for this page. ` +
        `${readBuffer().length} entries are held locally and will be retried on the next load. Last reason:`,
        e?.message || e,
      );
      // THE EMAIL TRIGGER. Only here — at the point the database sink has
      // genuinely given up, not on the first rejection, because the ordinary
      // boot race produces a rejection that the next attempt resolves. One
      // message carrying the whole batch, for the reason in
      // reportFailureBatchToBackend.
      reportFailureBatchToBackend(
        pending,
        `The Realtime Database rejected ${consecutiveFailures} consecutive writes. Last reason: ${e?.message || e}`,
      );
    } else {
      flushTimer = setTimeout(flush, ERROR_LOG_RETRY_BASE_MS * (2 ** (consecutiveFailures - 1)));
    }
  } finally {
    flushInFlight = false;
  }
};

const scheduleFlush = () => {
  if (flushTimer || suspended) return;
  flushTimer = setTimeout(flush, ERROR_LOG_FLUSH_INTERVAL_MS);
};

/**
 * Applies the three write guards. Returns the number of suppressed repeats to
 * attach to this entry, or `null` when the entry must be dropped entirely.
 * @param {string} fingerprint
 * @returns {number|null}
 */
const admitWrite = (fingerprint) => {
  const now = Date.now();

  if (sessionWriteCount >= ERROR_LOG_MAX_PER_SESSION) return null;

  writeTimestamps = writeTimestamps.filter(t => now - t < 60000);
  if (writeTimestamps.length >= ERROR_LOG_MAX_PER_MINUTE) return null;

  const prior = seen.get(fingerprint);
  if (prior && now - prior.lastWriteMs < ERROR_LOG_DEDUPE_WINDOW_MS) {
    // Inside the window: count it, write nothing. The count rides along on
    // the next record for this fingerprint, so repeats are reported rather
    // than discarded.
    prior.suppressed += 1;
    return null;
  }

  const suppressed = prior ? prior.suppressed : 0;
  seen.set(fingerprint, { lastWriteMs: now, suppressed: 0 });
  writeTimestamps.push(now);
  sessionWriteCount += 1;

  if (sessionWriteCount === ERROR_LOG_MAX_PER_SESSION) {
    nativeConsole.warn(`[errorLog] session cap of ${ERROR_LOG_MAX_PER_SESSION} entries reached; no further errors will be logged from this tab.`);
  }
  return suppressed;
};

/**
 * Queues one error for /errorLogs. The single entry point — every capture
 * path and every call site in app code goes through here.
 *
 * Never throws: a logging failure must not break the operation it was
 * attached to, exactly as logAuditEvent does for the audit trail.
 *
 * @param {object} params
 * @param {string} params.source - one of ERROR_LOG_SOURCES
 * @param {string} params.message - what went wrong
 * @param {Error|*} [params.error] - the thrown value, for stack and code
 * @param {string} [params.level] - ERROR_LOG_LEVELS.ERROR (default) or WARN
 * @param {object|null} [params.context] - structured extras (db path,
 *   operation, record id). Stored as a JSON string so the database rules stay
 *   a flat shape rather than validating an open-ended object.
 * @returns {void}
 */
export const logAppError = ({
  source = ERROR_LOG_SOURCES.MANUAL,
  message,
  error = null,
  level = ERROR_LOG_LEVELS.ERROR,
  context = null,
} = {}) => {
  try {
    // NOT gated on `db`. An earlier version returned here when Firebase had
    // not initialised, which silenced the logger completely in exactly the
    // situation it exists for: a build whose Firebase config is broken throws
    // on boot, and every one of those errors was discarded because the sink
    // that could not record them was also the thing deciding whether to
    // capture them. Capture always happens; only the DESTINATIONS vary.
    const text = String(message ?? (error && error.message) ?? '').trim();
    if (!text) return;
    if (matchesAny(text, ERROR_LOG_IGNORE_PATTERNS)) return;

    const stack = clip(error && error.stack ? error.stack : null, ERROR_LOG_MAX_STACK_LEN);
    const repeatCount = admitWrite(fingerprintOf(source, text, stack));
    if (repeatCount === null) return;

    let contextStr = null;
    if (context) {
      try {
        contextStr = clip(JSON.stringify(context), ERROR_LOG_MAX_CONTEXT_LEN);
      } catch (e) {
        contextStr = '[context could not be serialised]';
      }
    }

    const ts = Date.now();
    const entry = {
      timestamp: ts,
      // Denormalised so the admin screen can group by day and the prune can
      // select a day without re-deriving it from every timestamp.
      dayKey: toDayKey(ts),
      source,
      level,
      code: extractCode(error, text),
      message: clip(text, ERROR_LOG_MAX_MESSAGE_LEN),
      stack,
      context: contextStr,
      // Path and query only. The origin is the same for every entry in a
      // deployment, and a full href is mostly wasted characters.
      url: clip(`${window.location.pathname}${window.location.search}`, 300),
      screen: currentScreen,
      userAgent: clip(navigator.userAgent, 254),
      sessionId: SESSION_ID,
      actor: auth?.currentUser?.email || null,
      repeatCount,
    };

    // ---- Sink 1: the Realtime Database ----
    // Only possible when Firebase came up and this tab has not already given
    // up on it. `canUseDatabase` is what the backend is told, so a report can
    // say whether the database ever had a chance.
    const canUseDatabase = Boolean(db) && !suspended;
    if (canUseDatabase) {
      queue.push(entry);
      scheduleFlush();
    }

    // ---- Sinks 2 and 3: Vercel runtime logs, and email on failure ----
    // Sent for EVERY entry, immediately, and independently of the database.
    // Immediately rather than after the database outcome is known because the
    // page may not survive that long — a line that reaches the runtime log a
    // few seconds before its final status beats one that never arrives.
    //
    // PENDING does not trigger an email; UNAVAILABLE does, because there is no
    // later attempt coming that could change the outcome.
    reportErrorToBackend(
      entry,
      canUseDatabase ? ERROR_LOG_RTDB_STATUS.PENDING : ERROR_LOG_RTDB_STATUS.UNAVAILABLE,
    );
  } catch (e) {
    // The logger itself failing is not worth breaking anything over.
    nativeConsole.warn('[errorLog] could not queue an entry:', e?.message || e);
  }
};

/* ---- Capture ----------------------------------------------------------- */

/**
 * Starts capturing browser errors into /errorLogs. Safe to call more than
 * once; only the first call installs anything.
 *
 * Four sources, because no one of them sees everything:
 *  - window 'error'      — uncaught exceptions, plus failed <img>/<script>
 *                          loads, which carry no message at all
 *  - 'unhandledrejection'— a rejected promise nobody caught, which is where
 *                          most Firebase failures land
 *  - console.error       — everything the app already catches and reports
 *                          itself, captured with no call-site changes
 *  - console.warn        — Firebase-shaped warnings only. The Realtime
 *                          Database reports a rejected READ this way and
 *                          never throws, so this is the only path on which
 *                          `permission_denied` on a listener appears at all.
 *
 * @returns {void}
 */
export const installGlobalErrorLogging = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    // A resource that failed to load fires 'error' on the element, not the
    // window, and has no `event.error`. It matters (a missing bundle chunk
    // breaks the app outright) but reads completely differently.
    const target = event.target;
    if (target && target !== window && (target.src || target.href)) {
      logAppError({
        source: ERROR_LOG_SOURCES.RESOURCE,
        message: `Failed to load ${target.tagName?.toLowerCase() || 'resource'}: ${target.src || target.href}`,
      });
      return;
    }
    logAppError({
      source: ERROR_LOG_SOURCES.UNCAUGHT,
      message: event.message || 'Uncaught error',
      error: event.error,
      context: { file: event.filename || null, line: event.lineno || null, col: event.colno || null },
    });
    // Capture phase: resource errors do not bubble, so a listener on the
    // bubble phase never sees them.
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    logAppError({
      source: ERROR_LOG_SOURCES.UNHANDLED_REJECTION,
      message: reason instanceof Error ? reason.message : stringifyArgs([reason]),
      error: reason instanceof Error ? reason : null,
    });
  });

  console.error = (...args) => {
    nativeConsole.error(...args);
    const errArg = args.find(a => a instanceof Error) || null;
    logAppError({
      source: ERROR_LOG_SOURCES.CONSOLE_ERROR,
      message: stringifyArgs(args),
      error: errArg,
    });
  };

  console.warn = (...args) => {
    nativeConsole.warn(...args);
    const text = stringifyArgs(args);
    if (!matchesAny(text, ERROR_LOG_WARN_ALLOW_PATTERNS)) return;
    logAppError({
      source: ERROR_LOG_SOURCES.CONSOLE_WARN,
      message: text,
      level: ERROR_LOG_LEVELS.WARN,
    });
  };

  // Sign-in is what makes a write possible at all, so it is the real flush
  // trigger — not a timer. This is what closes the boot race: an error thrown
  // before AuthGuard's silent sign-in resolves is buffered by flush(), and
  // this callback sends it seconds later, in the same page load.
  //
  // It also covers the slower case. A visitor whose sign-in failed outright
  // carries their entries in localStorage; the next time they open the app
  // and authenticate, this fires and the backlog goes up with them.
  if (auth) {
    onAuthStateChanged(auth, (user) => {
      if (!user || suspended) return;
      // Sign-in arrived, so the outage the escalation timer was about to
      // report is not happening. Cancel it before it fires a false alarm.
      if (authEscalationTimer) { clearTimeout(authEscalationTimer); authEscalationTimer = null; }
      // A failed run is not held against the next one: a new identity is a
      // genuinely different attempt, not a retry of the one that failed.
      consecutiveFailures = 0;
      flush();
    });
  }

  // A tab closed or backgrounded mid-window would otherwise lose whatever is
  // still queued.
  //
  // On unload the queue is only PERSISTED, never sent: an async write cannot
  // be relied on to finish while the page is being torn down, and flush()
  // clears the buffer before awaiting — so attempting a send here is the one
  // way to actually lose entries. Buffering is synchronous, so it always
  // completes, and the next page load sends them. 'pagehide' is the event
  // that fires reliably on mobile Safari, where 'beforeunload' does not.
  const persistOnExit = () => {
    if (queue.length === 0) return;
    const leaving = queue;
    queue = [];
    bufferEntries(leaving);
  };
  window.addEventListener('pagehide', persistOnExit);
  window.addEventListener('beforeunload', persistOnExit);

  // Backgrounding is different: the page is still alive, so a real send is
  // both possible and worth doing — a tab left open on a phone may not come
  // back for hours.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });

  // Anything left over from a previous page load. Harmless when there is
  // nothing buffered, and when no one is signed in yet it simply re-buffers
  // and waits for the auth callback above.
  flush();
};

/**
 * Diagnostics for the admin screen: what this tab has done so far.
 *
 * `buffered` is the one worth surfacing — a non-zero count on a screen that
 * otherwise looks healthy means entries are being captured but not accepted,
 * which is a rules or auth problem rather than a quiet day.
 *
 * @returns {{sessionId: string, written: number, queued: number,
 *   buffered: number, suspended: boolean, cap: number}}
 */
export const getErrorLogSessionStats = () => ({
  sessionId: SESSION_ID,
  written: sessionWriteCount,
  queued: queue.length,
  buffered: readBuffer().length,
  suspended,
  cap: ERROR_LOG_MAX_PER_SESSION,
});

/**
 * Writes a deliberate test entry so an admin can prove the pipeline works
 * end to end on a live deployment without waiting for a real failure.
 *
 * Returns whether it actually reached the database, so the screen can tell
 * "written" apart from "captured but still held locally" — which is the
 * symptom of a missing rule or a failed sign-in, and the single most useful
 * thing this button can report.
 *
 * @returns {Promise<{sent: boolean, buffered: number}>}
 */
export const writeErrorLogTestEntry = async () => {
  logAppError({
    source: ERROR_LOG_SOURCES.MANUAL,
    message: 'Test entry written from the Error Logs screen.',
    level: ERROR_LOG_LEVELS.WARN,
    context: { test: true },
  });
  await flush();
  const buffered = readBuffer().length;
  return { sent: buffered === 0, buffered };
};

/* ---- Reading ----------------------------------------------------------- */

/**
 * Reads entries between two epoch-millisecond bounds, newest first.
 *
 * A one-shot get(), NOT a live onValue subscription — unlike the audit trail,
 * nothing here benefits from updating in place while an admin reads it, and a
 * live listener would re-download on every write from every visitor. The
 * screen refetches when the admin changes the range or presses Refresh.
 *
 * @param {number} startMs
 * @param {number} endMs
 * @returns {Promise<Array<object>>} entries, each with its Firebase `id`
 */
export const fetchErrorLogInRange = async (startMs, endMs) => {
  if (!db) return [];
  const q = query(dbRef(db, ERROR_LOG_NODE), orderByChild('timestamp'), startAt(startMs), endAt(endMs));
  const snapshot = await get(q);
  if (!snapshot.exists()) return [];
  const list = [];
  snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
  return list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
};

/**
 * Buckets entries into one group per calendar day, newest day first — the
 * "list of error.txt files" the admin screen shows. Each group is one
 * downloadable file.
 *
 * @param {Array<object>} entries
 * @returns {Array<{dayKey: string, entries: Array<object>, errorCount: number,
 *   warnCount: number, firstTimestamp: number, lastTimestamp: number}>}
 */
export const groupErrorsByDay = (entries) => {
  const byDay = new Map();
  (entries || []).forEach((entry) => {
    // Falls back to deriving the day from the timestamp so an entry written
    // before `dayKey` existed still lands in the right file.
    const key = entry.dayKey || toDayKey(entry.timestamp);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(entry);
  });

  return Array.from(byDay.entries())
    .map(([dayKey, dayEntries]) => {
      const sorted = [...dayEntries].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      return {
        dayKey,
        entries: sorted,
        errorCount: sorted.filter(e => e.level !== 'warn').length,
        warnCount: sorted.filter(e => e.level === 'warn').length,
        firstTimestamp: sorted[sorted.length - 1]?.timestamp || 0,
        lastTimestamp: sorted[0]?.timestamp || 0,
      };
    })
    .sort((a, b) => (b.dayKey < a.dayKey ? -1 : 1));
};

/**
 * Deletes every entry older than `days` whole days. Returns how many went.
 *
 * Reads only the keys it is about to delete (a bounded range query) and
 * removes them one path at a time through a single update() of nulls, so a
 * prune is one write no matter how many entries it clears.
 *
 * @param {number} days
 * @returns {Promise<number>}
 */
export const pruneErrorLogsOlderThan = async (days) => {
  if (!db) return 0;
  const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
  const q = query(dbRef(db, ERROR_LOG_NODE), orderByChild('timestamp'), endAt(cutoff));
  const snapshot = await get(q);
  if (!snapshot.exists()) return 0;

  const nulls = {};
  let count = 0;
  snapshot.forEach((child) => { nulls[child.key] = null; count += 1; });
  if (count === 0) return 0;

  await update(dbRef(db, ERROR_LOG_NODE), nulls);
  return count;
};

/**
 * Deletes one day's entries. Used by the per-file Delete on the admin screen.
 * @param {Array<object>} entries - the day's entries, as grouped above
 * @returns {Promise<number>}
 */
export const deleteErrorLogEntries = async (entries) => {
  if (!db || !entries || entries.length === 0) return 0;
  const nulls = {};
  entries.forEach((entry) => { if (entry.id) nulls[entry.id] = null; });
  const count = Object.keys(nulls).length;
  if (count === 0) return 0;
  await update(dbRef(db, ERROR_LOG_NODE), nulls);
  return count;
};

/* ---- .txt rendering ---------------------------------------------------- */

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * "2026-09-21 14:32:10" — sortable, unambiguous, and the same shape in the
 * header and in every entry so the file greps cleanly.
 * @param {number} ts
 * @returns {string}
 */
const formatStamp = (ts) => {
  if (!ts) return 'unknown';
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const RULE = '='.repeat(80);
const THIN_RULE = '-'.repeat(80);

/**
 * Renders entries as the plain-text file an admin downloads.
 *
 * This is the whole reason the log is stored as records: the file is built
 * here, at download time, from whatever the admin currently has filtered on
 * screen. Nothing text-shaped is ever stored.
 *
 * Entries come out OLDEST FIRST, the opposite of the on-screen order — a log
 * file is read top to bottom as a sequence of events, and the first failure
 * is usually the one that caused the rest.
 *
 * @param {Array<object>} entries
 * @param {object} [meta]
 * @param {string} [meta.clubName]
 * @param {string} [meta.label] - what this file covers ("2026-09-21", or a range)
 * @returns {string}
 */
export const buildErrorLogText = (entries, { clubName = 'Court Booking System', label = '' } = {}) => {
  const ordered = [...(entries || [])].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  const errorCount = ordered.filter(e => e.level !== 'warn').length;
  const warnCount = ordered.length - errorCount;

  const lines = [
    RULE,
    ' COURT BOOKING SYSTEM — ERROR LOG',
    RULE,
    ` Club       : ${clubName}`,
    ` Covers     : ${label || 'all entries'}`,
    ` Generated  : ${formatStamp(Date.now())}`,
    ` Entries    : ${ordered.length} (${errorCount} error${errorCount === 1 ? '' : 's'}, ${warnCount} warning${warnCount === 1 ? '' : 's'})`,
    RULE,
    '',
  ];

  if (ordered.length === 0) {
    lines.push('No entries.', '');
    return lines.join('\n');
  }

  ordered.forEach((entry, index) => {
    const level = (entry.level === 'warn' ? 'WARN ' : 'ERROR');
    lines.push(`[${String(index + 1).padStart(4, '0')}] ${formatStamp(entry.timestamp)}  ${level}  ${entry.source || 'unknown'}`);
    if (entry.code) lines.push(`       code     : ${entry.code}`);
    lines.push(`       message  : ${entry.message || '(none)'}`);
    if (entry.screen) lines.push(`       screen   : ${entry.screen}`);
    if (entry.url) lines.push(`       url      : ${entry.url}`);
    if (entry.actor) lines.push(`       actor    : ${entry.actor}`);
    if (entry.sessionId) lines.push(`       session  : ${entry.sessionId}`);
    // Only when it actually repeated — a "repeats: 0" line on every entry
    // would bury the handful that matter.
    if (entry.repeatCount) lines.push(`       repeats  : +${entry.repeatCount} identical within the dedupe window`);
    if (entry.context) lines.push(`       context  : ${entry.context}`);
    if (entry.userAgent) lines.push(`       agent    : ${entry.userAgent}`);
    if (entry.stack) {
      lines.push('       stack    :');
      entry.stack.split('\n').forEach((frame) => { lines.push(`         ${frame.trim()}`); });
    }
    lines.push(THIN_RULE);
  });

  lines.push('');
  return lines.join('\n');
};

/**
 * Turns the rendered text into a .txt the browser saves. Same object-URL
 * approach the receipt/export downloads already use.
 *
 * @param {string} text
 * @param {string} filename
 * @returns {void}
 */
export const downloadTextFile = (text, filename) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Released on the next tick rather than immediately: revoking synchronously
  // cancels the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * Builds and saves one day's (or one range's) entries as a .txt.
 * @param {Array<object>} entries
 * @param {object} [meta] - see buildErrorLogText
 * @returns {void}
 */
export const downloadErrorLogTxt = (entries, meta = {}) => {
  const text = buildErrorLogText(entries, meta);
  const slug = (meta.label || 'all').replace(/[^0-9a-zA-Z-]+/g, '_');
  downloadTextFile(text, `errors-${slug}.txt`);
};
